/**
 * The word map: every word the child knows, and what sits next to what.
 *
 * **Why a map and not a list.** A list of 1,665 words says "you have done a
 * lot". A map says "these two belong together" — which is the thing a list
 * cannot show and the thing that lets a child guess a word nobody taught them
 * from the company it keeps.
 *
 * **Colour means WHEN, not what.** Each blob is coloured by how long it has
 * been since the child last met that word: this week is bright, this month
 * warm, this year cooler, older faded back. An earlier version coloured by a
 * hash of the word's own letters, which looked cheerful and meant nothing at
 * all — five colours assigned at random are decoration, not information.
 *
 * Recency is the right thing to colour by because it is the one fact a child
 * can act on. "I have not seen this one in a year" is a reason to tap it.
 *
 * **It only ever grows.** A word appears when it is met and never leaves. A
 * word that is forgotten goes back to being a seedling but keeps its place.
 * Same rule as the garden (D-048), drawn differently.
 *
 * **Things to do with it**, because a picture you can only look at gets looked
 * at once: filter by time, tap a word to light up everything joined to it,
 * drag a word to move it, and double-tap to travel to its neighbourhood.
 *
 * **There is always a way back.** Travelling to a word's neighbourhood used
 * to be a one-way door with only a small link below the map to undo it, and
 * the link went all the way home however deep you had gone. So the map kept
 * a child's whole journey but gave back nothing of it. Now the way in is
 * remembered as a trail: tapping the background steps back one word, the
 * trail is drawn above the map and every step on it can be tapped, and
 * Escape does the same for anyone on a keyboard.
 *
 * The layout is a small spring simulation: linked words pull together,
 * everything pushes apart, and the middle pulls everything gently inwards. A
 * hundred lines rather than a graph library, because a graph library is 200kB
 * and this needs to be cute rather than correct.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { ExitButton } from '../components/ExitButton'
import { api, type WordMap as MapData, type WordMapNode } from '../api'
import { radius, theme, TAP_MIN } from '../theme'
import { Button, Card, Heading, Page } from '../components/ui'
import { ErrorScreen, Loading } from '../components/States'
import { say } from '../lib/speech'
import { Trace, TraceRegion } from '../lib/trace'
import { useViewport } from '../lib/screen'

/**
 * The drawing is one shape on a laptop and another on a phone.
 *
 * Everything inside scales with the viewBox, so a 720-wide drawing squeezed
 * into a 328-wide phone renders its 13px labels at about 6px — there, but
 * unreadable. A portrait box roughly the shape of the screen it lands on
 * scales by about 0.85 instead of 0.45, and the words can be read.
 *
 * Fewer words come back too. Thirty-six blobs in a phone-sized box is a grey
 * smudge; fourteen is a map.
 */
const WIDE = { w: 720, h: 520, limit: null as number | null }
const NARROW_MAP = { w: 380, h: 620, limit: 14 }

type Placed = WordMapNode & { x: number; y: number; vx: number; vy: number; r: number }

/**
 * What each colour means, oldest first so the legend reads as a timeline.
 *
 * Five steps rather than a smooth gradient: a child can hold five categories
 * and cannot read a continuous scale, and "this week" is a thing you can point
 * at where "0.3 on the recency axis" is not.
 */
const AGES = [
  { within: 7, label: 'This week', pick: () => theme.green },
  { within: 30, label: 'This month', pick: () => theme.orange },
  { within: 180, label: '6 months', pick: () => theme.sandDark },
  { within: 365, label: 'A year', pick: () => theme.blue },
  { within: Infinity, label: 'Longer ago', pick: () => theme.faint },
] as const

function ageOf(node: WordMapNode) {
  const days = node.days_since
  if (days === null) return AGES[AGES.length - 1]!
  return AGES.find(a => days <= a.within) ?? AGES[AGES.length - 1]!
}

/** How big a word is drawn: more practice, bigger blob. Clamped so a brand
 *  new word is still tappable and a much-practised one stays on screen. */
function radiusFor(n: WordMapNode, narrow: boolean): number {
  const small = narrow ? 18 : 22
  const big = narrow ? 32 : 40
  return Math.max(small, Math.min(big, small + n.successes * 1.6))
}

/**
 * Lay the words out with springs.
 *
 * Deterministic: the same words always start in the same places, so the map
 * does not rearrange itself every time a child opens it. A map that looks
 * different each visit is not a place, and the whole point is that it becomes
 * somewhere familiar.
 */
function layout(
  nodes: WordMapNode[], edges: MapData['edges'],
  WIDTH: number, HEIGHT: number, narrow: boolean,
): Placed[] {
  const placed: Placed[] = nodes.map((n, i) => {
    const angle = (i / Math.max(1, nodes.length - 1)) * Math.PI * 2
    const reach = Math.min(WIDTH, HEIGHT) * 0.29
    const spread = n.focus ? 0 : reach + (i % 3) * (reach * 0.37)
    return {
      ...n,
      x: WIDTH / 2 + Math.cos(angle) * spread,
      y: HEIGHT / 2 + Math.sin(angle) * spread * 0.72,
      vx: 0, vy: 0,
      r: radiusFor(n, narrow),
    }
  })

  const index = new Map(placed.map((p, i) => [p.id, i]))
  const links: { a: number; b: number; story: boolean }[] = []
  for (const e of edges) {
    const a = index.get(e.a), b = index.get(e.b)
    if (a === undefined || b === undefined) continue
    links.push({ a, b, story: e.kind === 'story' })
  }

  for (let step = 0; step < 320; step++) {
    for (let i = 0; i < placed.length; i++) {
      for (let j = i + 1; j < placed.length; j++) {
        const a = placed[i]!, b = placed[j]!
        let dx = b.x - a.x, dy = b.y - a.y
        let d = Math.hypot(dx, dy) || 0.01
        const want = a.r + b.r + 16
        if (d < want) {
          const push = (want - d) / d * 0.5
          dx *= push; dy *= push
          a.vx -= dx; a.vy -= dy
          b.vx += dx; b.vy += dy
        }
      }
    }
    for (const link of links) {
      const a = placed[link.a]!, b = placed[link.b]!
      const dx = b.x - a.x, dy = b.y - a.y
      const d = Math.hypot(dx, dy) || 0.01
      const want = (link.story ? 118 : 150) * (narrow ? 0.62 : 1)
      const pull = (d - want) / d * (link.story ? 0.045 : 0.028)
      a.vx += dx * pull; a.vy += dy * pull
      b.vx -= dx * pull; b.vy -= dy * pull
    }
    for (const p of placed) {
      if (p.focus) {
        p.vx += (WIDTH / 2 - p.x) * 0.08
        p.vy += (HEIGHT / 2 - p.y) * 0.08
      } else {
        p.vx += (WIDTH / 2 - p.x) * 0.004
        p.vy += (HEIGHT / 2 - p.y) * 0.006
      }
      p.x += p.vx; p.y += p.vy
      p.vx *= 0.82; p.vy *= 0.82
      p.x = Math.max(p.r + 6, Math.min(WIDTH - p.r - 6, p.x))
      p.y = Math.max(p.r + 6, Math.min(HEIGHT - p.r - 6, p.y))
    }
  }
  return placed
}

function whenText(node: WordMapNode): string {
  const d = node.days_since
  if (d === null) return 'not met yet'
  if (d <= 1) return 'today'
  if (d < 7) return `${d} days ago`
  if (d < 31) return `${Math.round(d / 7)} weeks ago`
  if (d < 365) return `${Math.round(d / 30)} months ago`
  const years = d / 365
  return years < 1.6 ? 'about a year ago' : `${years.toFixed(0)} years ago`
}

/** One step of the trail above the map. */
function Crumb({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        fontFamily: 'inherit', fontSize: 14, fontWeight: 600,
        color: theme.muted, background: 'transparent',
        border: 'none', borderBottom: `1.5px solid ${theme.border}`,
        padding: '2px 0', cursor: 'pointer',
      }}
    >
      {label}
    </button>
  )
}


/** The same step, as a proper button under the map. */
function BackLink({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        fontFamily: 'inherit', fontSize: 14, fontWeight: 600,
        color: theme.muted, background: 'transparent',
        border: `1px solid ${theme.border}`, borderRadius: radius.sm,
        padding: '8px 12px', minHeight: TAP_MIN, cursor: 'pointer',
      }}
    >
      {label}
    </button>
  )
}


export function WordMap({
  learnerId, onHome, onGarden,
}: { learnerId: string; onHome: () => void; onGarden: () => void }) {
  const [data, setData] = useState<MapData | null>(null)
  const [error, setError] = useState<unknown>(null)
  /**
   * How the child got here: ['animal', 'predator'] means they started on the
   * whole map, went into `animal`, and from there into `predator`.
   *
   * A trail rather than a single word because "one level up" has to mean the
   * word they came FROM. Sending them back to the whole map from three words
   * deep throws away the journey they just made, which is the part they were
   * enjoying.
   */
  const [trail, setTrail] = useState<string[]>([])
  const focus = trail[trail.length - 1] ?? ''
  const [days, setDays] = useState<number | null>(null)
  const [picked, setPicked] = useState<string | null>(null)
  const [dragged, setDragged] = useState<string | null>(null)
  const [nudge, setNudge] = useState<Record<string, { x: number; y: number }>>({})
  const svgRef = useRef<SVGSVGElement>(null)
  const { narrow } = useViewport()
  const shape = narrow ? NARROW_MAP : WIDE

  const goInto = (word: string) => setTrail((s) => [...s, word])

  /**
   * One step back: first un-light the chosen word, then leave the
   * neighbourhood.
   *
   * Two steps rather than one because they are two different things the child
   * did, and undoing them in the order they were done is what "back" means
   * everywhere else.
   */
  const goUp = () => {
    if (picked) { setPicked(null); return }
    setTrail((s) => s.slice(0, -1))
  }
  const canGoUp = Boolean(picked) || trail.length > 0

  const load = () => {
    setError(null)
    api.wordMap(learnerId, focus, days, shape.limit)
      .then(setData).catch(setError)
  }
  useEffect(() => { load() }, [learnerId, focus, days, shape.limit])
  useEffect(() => { setNudge({}); setPicked(null) }, [focus, days])

  // Escape, for whoever is on a laptop. Same step as tapping the background.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      if (!picked && trail.length === 0) return
      e.preventDefault()
      if (picked) setPicked(null)
      else setTrail((s) => s.slice(0, -1))
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [picked, trail.length])

  const placed = useMemo(
    () => (data ? layout(data.nodes, data.edges, shape.w, shape.h, narrow) : []),
    [data, shape.w, shape.h, narrow],
  )

  /** Everything joined to the tapped word, so the rest can step back. */
  const lit = useMemo(() => {
    if (!picked || !data) return null
    const near = new Set<string>([picked])
    for (const e of data.edges) {
      if (e.a === picked) near.add(e.b)
      if (e.b === picked) near.add(e.a)
    }
    return near
  }, [picked, data])

  if (error) return <ErrorScreen error={error} onRetry={load} onHome={onHome} />
  if (!data) return <Loading what="Drawing your map" />

  const at = (p: Placed) => {
    const n = nudge[p.id]
    return { x: p.x + (n?.x ?? 0), y: p.y + (n?.y ?? 0) }
  }
  const byId = new Map(placed.map(p => [p.id, p]))
  const chosen = picked ? placed.find(p => p.id === picked) ?? null : null

  /** Drag, in the SVG's own coordinates rather than the screen's. */
  const toSvg = (e: React.PointerEvent) => {
    const box = svgRef.current?.getBoundingClientRect()
    if (!box) return null
    return {
      x: (e.clientX - box.left) / box.width * shape.w,
      y: (e.clientY - box.top) / box.height * shape.h,
    }
  }

  return (
    <Page width={780}>
      <Heading sub={
        <>
          <Trace id="calc.rows in T7 learner_word this child has met">
            {data.total_words.toLocaleString()} words
          </Trace>
          {' · '}
          <Trace id="calc.rows in T7 with grown_at set"
                 title="a bar that only rises: a word never leaves this map">
            {data.total_grown} fully grown
          </Trace>
        </>
      }>
        Your word map
      </Heading>

      {/* When did you last see it? The one filter worth having, and the counts
          say what each one will show before it is tapped. */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
        {data.bands.map((band) => {
          const on = (band.days ?? null) === days
          return (
            <button
              key={band.label}
              type="button"
              disabled={band.count === 0}
              onClick={() => setDays(band.days ?? null)}
              style={{
                fontFamily: 'inherit', fontSize: 14,
                fontWeight: on ? 800 : 600,
                color: on ? theme.orangeDark : band.count ? theme.muted : theme.fainter,
                background: on ? theme.orangePale : theme.card,
                border: `2px solid ${on ? theme.orange : theme.border}`,
                borderRadius: radius.pill, padding: '8px 14px',
                minHeight: TAP_MIN, cursor: band.count ? 'pointer' : 'default',
              }}
            >
              {band.label}
              <span style={{ opacity: 0.6, marginLeft: 6, fontWeight: 600 }}>
                {band.count}
              </span>
            </button>
          )
        })}
      </div>

      {/* Where you are, and every step of how you got here. Each one goes
          back to that point rather than all the way home. */}
      {trail.length ? (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap',
          marginBottom: 12, fontSize: 14,
        }}>
          <Crumb label="All your words" onClick={() => setTrail([])} />
          {trail.map((word, i) => (
            <span key={`${word}-${i}`} style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
            }}>
              <span style={{ color: theme.fainter }}>›</span>
              {i === trail.length - 1 ? (
                <span style={{ fontWeight: 800, color: theme.orangeDark }}>
                  {word}
                </span>
              ) : (
                <Crumb label={word}
                       onClick={() => setTrail((s) => s.slice(0, i + 1))} />
              )}
            </span>
          ))}
        </div>
      ) : null}

      {data.empty_message ? (
        <Card style={{ textAlign: 'center', color: theme.muted, padding: 30 }}>
          {data.empty_message}
        </Card>
      ) : (
        <>
          <TraceRegion id="T7" note="joined by T9 reading items and the synonym cache">
          <Card style={{ padding: 0, overflow: 'hidden', background: theme.cardWarm }}>
            <svg
              ref={svgRef}
              viewBox={`0 0 ${shape.w} ${shape.h}`}
              style={{ width: '100%', height: 'auto', display: 'block',
                       touchAction: 'none' }}
              role="img"
              aria-label={`A map of ${data.nodes.length} words and how they connect`}
              onPointerMove={(e) => {
                if (!dragged) return
                const p = toSvg(e); const base = byId.get(dragged)
                if (!p || !base) return
                setNudge((n) => ({ ...n,
                  [dragged]: { x: p.x - base.x, y: p.y - base.y } }))
              }}
              onPointerUp={() => setDragged(null)}
              onPointerLeave={() => setDragged(null)}
            >
              {/* The background, and the only reason it exists: something
                  has to receive a tap that is not on a word. It is drawn
                  first, so every blob and line sits on top of it and keeps
                  its own tap. */}
              <rect
                x={0} y={0} width={shape.w} height={shape.h}
                fill="transparent"
                style={{ cursor: canGoUp ? 'zoom-out' : 'default' }}
                onClick={() => { if (canGoUp) goUp() }}
              />

              <defs>
                <filter id="soft" x="-30%" y="-30%" width="160%" height="160%">
                  <feDropShadow dx="0" dy="2" stdDeviation="3"
                                floodColor={theme.deep} floodOpacity="0.14" />
                </filter>
              </defs>

              {data.edges.map((e, i) => {
                const a = byId.get(e.a), b = byId.get(e.b)
                if (!a || !b) return null
                const pa = at(a), pb = at(b)
                const story = e.kind === 'story'
                const dim = lit ? !(lit.has(e.a) && lit.has(e.b)) : false
                return (
                  <line
                    key={i}
                    x1={pa.x} y1={pa.y} x2={pb.x} y2={pb.y}
                    stroke={story ? theme.green : theme.blueTint}
                    strokeWidth={story ? 3 : 2}
                    strokeLinecap="round"
                    strokeDasharray={story ? undefined : '2 7'}
                    opacity={dim ? 0.08 : story ? 0.5 : 0.65}
                    style={{ transition: 'opacity 160ms ease' }}
                  />
                )
              })}

              {placed.map((n) => {
                const age = ageOf(n)
                const colour = age.pick()
                const p = at(n)
                const dim = lit ? !lit.has(n.id) : false
                const isPicked = n.id === picked
                return (
                  <g
                    key={n.id}
                    transform={`translate(${p.x} ${p.y})`}
                    style={{ cursor: 'grab', opacity: dim ? 0.18 : 1,
                             transition: 'opacity 160ms ease' }}
                    onPointerDown={() => setDragged(n.id)}
                    onClick={() => {
                      // Tapping the one already chosen travels to it. Tapping
                      // a different one just lights it up — so exploring is
                      // one tap and moving is two, which is the right way
                      // round for a child poking at a picture.
                      if (isPicked) { goInto(n.word); return }
                      setPicked(n.id); say(n.word)
                    }}
                    role="button"
                    aria-label={`${n.word}, last met ${whenText(n)}`}
                  >
                    <circle
                      r={n.r}
                      fill={theme.card}
                      stroke={colour}
                      strokeWidth={isPicked ? 5 : n.focus ? 4 : 3}
                      filter="url(#soft)"
                    />
                    {/* A ring of the age colour, filled in proportion to how
                        well the word is known — so size is practice, colour is
                        when, and fill is how far along. */}
                    <circle
                      r={n.r - 4}
                      fill={colour}
                      opacity={0.10 + Math.min(n.successes, 12) * 0.035}
                    />
                    <text y={-n.r + 2} textAnchor="middle" fontSize="15">
                      {n.grown ? '🌿' : '🌱'}
                    </text>
                    <text
                      y={6}
                      textAnchor="middle"
                      fontSize={n.word.length > 10
                        ? (narrow ? 10 : 11)
                        : (narrow ? 12 : 13)}
                      fontWeight={isPicked || n.focus ? 800 : 700}
                      fill={theme.text}
                    >
                      {n.word.length > 13 ? n.word.slice(0, 12) + '…' : n.word}
                    </text>
                  </g>
                )
              })}
            </svg>
          </Card>
          </TraceRegion>

          {/* What the colours mean. Without this the map is pretty and mute. */}
          <div style={{
            display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center',
            fontSize: 12, color: theme.muted, margin: '12px 2px 0',
          }}>
            <span style={{ fontWeight: 700, color: theme.text2 }}>Last seen:</span>
            {AGES.map((a) => (
              <span key={a.label}
                    style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <span style={{
                  width: 13, height: 13, borderRadius: '50%',
                  border: `3px solid ${a.pick()}`, background: theme.card,
                }} />
                {a.label}
              </span>
            ))}
          </div>
          <div style={{
            display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center',
            fontSize: 12, color: theme.muted, margin: '8px 2px 0',
          }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <svg width="24" height="8"><line x1="1" y1="4" x2="23" y2="4"
                stroke={theme.green} strokeWidth="3" strokeLinecap="round" /></svg>
              same story
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <svg width="24" height="8"><line x1="1" y1="4" x2="23" y2="4"
                stroke={theme.blueTint} strokeWidth="2" strokeDasharray="2 7"
                strokeLinecap="round" /></svg>
              similar meaning
            </span>
            <span>🌿 grown</span>
            <span>🌱 growing</span>
            <span style={{ color: theme.fainter }}>
              tap to light up · tap again to travel · drag to move
            </span>
          </div>

          <div aria-live="polite" style={{ marginTop: 14 }}>
            {chosen ? (
              <Card style={{ borderColor: ageOf(chosen).pick(),
                             background: theme.cardWarm }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 10,
                              flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 22, fontWeight: 800, color: theme.text }}>
                    {chosen.grown ? '🌿' : '🌱'} {chosen.word}
                  </span>
                  <span style={{
                    fontSize: 13, fontWeight: 700, color: ageOf(chosen).pick(),
                  }}>
                    last seen {whenText(chosen)}
                  </span>
                  <span style={{ fontSize: 13, color: theme.muted }}>
                    {chosen.links === 0
                      ? 'no links yet'
                      : `${chosen.links} link${chosen.links === 1 ? '' : 's'}`}
                  </span>
                </div>
                {chosen.definition ? (
                  <p style={{ margin: '8px 0 0', fontSize: 16, lineHeight: 1.5,
                              color: theme.text2 }}>
                    <Trace id="T4.definition">{chosen.definition}</Trace>
                  </p>
                ) : null}
                <div style={{ display: 'flex', gap: 10, marginTop: 14,
                              flexWrap: 'wrap' }}>
                  <Button tone="child" onClick={() => goInto(chosen.word)}>
                    Look around {chosen.word}
                  </Button>
                  <Button tone="ghost" onClick={() => say(chosen.word)}>
                    🔊 Say it
                  </Button>
                  <Button tone="ghost" onClick={() => setPicked(null)}>
                    Show everything
                  </Button>
                </div>
              </Card>
            ) : null}
          </div>

          {/* Said out loud, because a tap target with no edges is a tap
              target nobody finds. */}
          <div style={{
            marginTop: 10, fontSize: 13, color: theme.fainter,
            textAlign: 'center',
          }}>
            {picked
              ? 'Tap the same word again to go there, or tap the background to let go of it.'
              : trail.length
                ? `Tap the background to go back to ${trail.length > 1 ? trail[trail.length - 2] : 'all your words'}.`
                : 'Tap a word to light up everything joined to it. Tap it again to go there.'}
          </div>

          {trail.length ? (
            <div style={{ marginTop: 14, display: 'flex', gap: 10,
                          flexWrap: 'wrap' }}>
              <BackLink
                label={trail.length > 1
                  ? `\u2190 Back to ${trail[trail.length - 2]}`
                  : '\u2190 Back to the whole map'}
                onClick={() => setTrail((s) => s.slice(0, -1))}
              />
              {trail.length > 1 ? (
                <BackLink label="\u2190 All your words"
                          onClick={() => setTrail([])} />
              ) : null}
            </div>
          ) : null}
        </>
      )}

      <div style={{ display: 'flex', gap: 10, marginTop: 26, flexWrap: 'wrap' }}>
        <Button tone="quiet" onClick={onGarden}>My garden</Button>
        <ExitButton onExit={onHome} />
      </div>
    </Page>
  )
}
